import React, { useState, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap'
import useDebounce from '../common/useDebounce'
import API from '../../utils/API'
import { useErrorService } from '@abroad/components'
import { string } from 'yup'

const emailValidationSchema = string()
  .email('Enter a valid email address (e.g., name@example.com).')
  .matches(
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  )
  .max(255)
  .required('Enter a valid email address')

const SearchUserModal = ({
  showModal,
  handleClose,
  onUserFound,
  onUserNotFound,
  search,
  setSearch,
  questParticipants,
}) => {
  const [searchData, setSearchData] = useState([])
  const [isSearch, setIsSearch] = useState(false)
  const [showSuggest, setShowSuggest] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [findHint, setFindHint] = useState('')
  const Error = useErrorService()
  const debouncedSearch = useDebounce(search, 500)
  // Skip the next debounced search after picking a suggestion (already have the user).
  const skipNextSearchRef = useRef(false)
  // Invalidate in-flight searches when the query changes or a suggestion is picked.
  const searchRequestIdRef = useRef(0)

  const isOnRoster = (email) =>
    questParticipants?.some(
      (participant) =>
        participant?.userId?.email?.toLowerCase() ===
        String(email || '')
          .trim()
          .toLowerCase(),
    )

  useEffect(() => {
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false
      setIsSearch(false)
      return
    }

    const getSearchData = async () => {
      const requestId = ++searchRequestIdRef.current
      setIsSearch(true)
      try {
        const { data } = await API.AdminAPI.coachClient.getSearchUser(
          debouncedSearch,
          {
            includeAdmin: true,
            includeCoach: true,
          },
        )
        if (requestId !== searchRequestIdRef.current) return

        const filtered = (data || []).filter((user) => !isOnRoster(user.email))
        setSearchData(filtered)

        const query = String(debouncedSearch || '').trim()
        if (filtered.length === 0 && query) {
          try {
            await emailValidationSchema.validate(query)
            if (!isOnRoster(query)) {
              setFindHint('No account yet — continue to invite.')
            } else {
              setFindHint('')
            }
          } catch {
            setFindHint('')
          }
        } else {
          setFindHint('')
        }
      } catch (e) {
        if (requestId !== searchRequestIdRef.current) return
        Error.showError(e)
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setIsSearch(false)
        }
      }
    }
    if (debouncedSearch !== '') getSearchData()
    else {
      searchRequestIdRef.current += 1
      setSearchData([])
      setFindHint('')
      setIsSearch(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const validateEmail = async (email) => {
    try {
      await emailValidationSchema.validate(email)
      setEmailError('')
      return true
    } catch (err) {
      setEmailError(err.message)
      return false
    }
  }

  const handleContinue = async () => {
    const email = String(search || '').trim()
    setEmailError('')

    const isValidEmail = await validateEmail(email)
    if (!isValidEmail) {
      return
    }

    if (isOnRoster(email)) {
      setEmailError('This person is already on the roster.')
      return
    }

    const exactMatchUser = searchData.find(
      (user) => user.email.toLowerCase() === email.toLowerCase(),
    )

    if (exactMatchUser) {
      const isUserAlreadyParticipating = questParticipants?.some(
        (participant) => participant?.userId?._id === exactMatchUser._id,
      )
      if (isUserAlreadyParticipating) {
        setEmailError('This person is already on the roster.')
        return
      }
      onUserFound(exactMatchUser)
      return
    }

    onUserNotFound(email)
  }

  const handleSearchChange = (value) => {
    skipNextSearchRef.current = false
    setSearch(value)
    setEmailError('')
    setSearchData([])
    setFindHint('')
    setShowSuggest(true)
  }

  const handleSelectSuggestion = (userInfo) => {
    skipNextSearchRef.current = true
    searchRequestIdRef.current += 1
    setSearch(userInfo.email)
    setSearchData([userInfo])
    setShowSuggest(false)
    setFindHint('')
    setEmailError('')
    setIsSearch(false)
  }

  const canContinue =
    String(search || '').trim() !== '' && !isSearch && emailError === ''

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      dialogClassName='experience-add-part-dialog'
      contentClassName='experience-add-part-card'>
      <Modal.Body className='p-0'>
        <div className='experience-add-part'>
          <h2>Add participant</h2>
          <p className='experience-add-part-lead'>
            Search by email. If they have no account, you can invite them.
          </p>
          <div className='experience-f'>
            <label className='experience-f-label' htmlFor='addPartEmail'>
              Email
            </label>
            <div className='experience-suggest-wrap'>
              <input
                id='addPartEmail'
                className='form-control experience-f-control'
                type='email'
                value={search}
                placeholder='name@example.com'
                autoComplete='off'
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    if (canContinue) handleContinue()
                  }
                }}
              />
              {showSuggest && searchData.length > 0 && search !== '' ? (
                <ul className='experience-suggest-list'>
                  {searchData.map((userInfo) => (
                    <li
                      key={userInfo._id}
                      onMouseDown={() => handleSelectSuggestion(userInfo)}>
                      <b>{userInfo.fullName}</b>
                      <small>{userInfo.email}</small>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            {findHint ? (
              <p className='experience-add-part-hint'>{findHint}</p>
            ) : null}
            {emailError ? (
              <p className='experience-add-part-error'>{emailError}</p>
            ) : null}
          </div>
          <div className='experience-modal-actions'>
            <button
              type='button'
              className='btn-outline-experience'
              onClick={handleClose}>
              Cancel
            </button>
            <button
              type='button'
              className='btn-navy'
              disabled={!canContinue}
              onClick={handleContinue}>
              {isSearch ? 'Searching…' : 'Continue'}
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default SearchUserModal
